import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FaArrowLeft, FaStar } from "react-icons/fa";
import { toast } from "react-toastify";
import {
  addComment,
  type TAddCommentRequest,
  type TAddCommentResponse,
} from "../../services/comment";
import { commentSchema, type CommentFormData } from "./types";

interface AddCommentProps {
  lessonId: number | null;
}

const AddComment = ({ lessonId }: AddCommentProps) => {
  const [rating, setRating] = useState(0);
  const [lesson, setLesson] = useState<number>(lessonId || 11);
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation<
    TAddCommentResponse,
    { message: string; statusCode: number },
    TAddCommentRequest["payload"]
  >({
    mutationFn: addComment,
    onSuccess: () => {
      toast.success("نظر شما با موفقیت ثبت شد 🌟");
    },
    onError: (error) => {
      const status = error.statusCode;
      if (status === 400) toast.error("داده‌های ارسالی نامعتبر هستند ❌");
      else if (status === 401)
        toast.error("برای ارسال نظر باید وارد حساب کاربری شوید 🔒");
      else if (status === 403) toast.error("شما مجوز ارسال نظر را ندارید 🚫");
      else toast.error("خطایی در ارسال نظر رخ داد، لطفاً دوباره تلاش کنید ⚠️");
    },
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CommentFormData>({
    resolver: zodResolver(commentSchema),
    defaultValues: {
      lesson: lesson,
    },
  });

  useEffect(() => {
    if (lessonId) {
      setLesson(lessonId);
      setValue("lesson", lessonId);
    }
  }, [lessonId, setValue]);

  const onSubmit = (data: CommentFormData) => {
    console.log(data);
    mutate(
      {
        lesson: data.lesson,
        rate: data.rate as number,
        text: data.text,
      },
      {
        onSuccess: () => {
          reset();
          setRating(0);
          queryClient.invalidateQueries({ queryKey: ["lesson-comments"] });
        },
      }
    );
  };

  const handleStarClick = (value: number) => {
    setRating(value);
    setValue("rate", value);
  };

  return (
    <div className="flex flex-col items-center justify-center gap-8">
      <h4 className="text-2xl md:text-[40px] font-kalameh font-semibold">
        نظرتو اینجا بگو!
      </h4>

      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar
            key={star}
            className={`text-[40px] md:text-[66px] cursor-pointer transition-colors duration-200 ${
              star <= rating ? "text-yellow-400" : "text-gray-300"
            }`}
            onClick={() => handleStarClick(star)}
          />
        ))}
      </div>
      {errors.rate && (
        <p className="text-red-500 text-sm font-kalameh">
          {errors.rate.message}
        </p>
      )}

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-10/12 md:w-7/12 mx-auto flex flex-col items-center gap-4 relative"
      >
        <textarea
          {...register("text")}
          id="comment"
          placeholder="نظر خود را بنویسید..."
          className="border border-[#898989] w-full h-52 md:h-48 rounded-4xl p-3 font-kalameh"
        />
        {errors.text && (
          <p className="text-red-500 text-sm self-start font-kalameh">
            {errors.text.message}
          </p>
        )}

        <button
          type="submit"
          disabled={isPending}
          className={`
            font-kalameh text-white rounded-full
            w-[130px] h-[39px] 
            md:w-[239px] md:h-[70px]
            bg-gradient-to-l from-[#54C6F9] to-[#A36AFF]
            flex items-center justify-center gap-4 md:gap-8
            text-[20px] md:text-3xl whitespace-nowrap
            ${isPending ? "opacity-50 cursor-not-allowed" : ""}
            absolute left-1/2 -translate-x-1/2 bottom-0 translate-y-1/2
            md:static md:translate-x-0 md:translate-y-0
          `}
        >
          {isPending ? "در حال ارسال..." : "ارسال نظر"}
          <FaArrowLeft className="md:w-6 w-4 md:h-6 h-4" />
        </button>
      </form>
    </div>
  );
};

export default AddComment;
